import { useCallback, useMemo } from 'react'
import PhoneInputWithCountry from 'react-phone-number-input'

import { useFieldDescribedBy } from '@/components/sui/field'
import { useLocale } from '@/hooks/use-locale'
import { resolveDefaultCountry } from '@/lib/locale-country'
import { cn } from '@/lib/utils'

import 'react-phone-number-input/style.css'
import './phone-input.css'

interface PhoneInputProps {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  disabled?: boolean
  className?: string
  id?: string
  'aria-invalid'?: boolean
  'aria-label'?: string
  inputRef?: React.RefCallback<HTMLInputElement>
  country?: string
}

export function PhoneInput({
  value,
  onChange,
  onBlur,
  placeholder,
  disabled,
  className,
  id,
  'aria-invalid': ariaInvalid,
  'aria-label': ariaLabel,
  inputRef,
  country,
}: PhoneInputProps) {
  const locale = useLocale()
  const describedBy = useFieldDescribedBy()

  const defaultCountry = useMemo(
    () => resolveDefaultCountry(locale, country),
    [country, locale],
  )

  const wrapperRef = useCallback(
    (node: HTMLDivElement | null) => {
      inputRef?.(node?.querySelector('input') ?? null)
    },
    [inputRef],
  )

  return (
    <div
      ref={wrapperRef}
      className={cn('phone-input-wrapper', className)}
      aria-invalid={ariaInvalid}
    >
      <PhoneInputWithCountry
        international
        defaultCountry={defaultCountry}
        value={value}
        onChange={(val) => onChange(val || '')}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        id={id}
        aria-invalid={ariaInvalid}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
      />
    </div>
  )
}
